import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  apiReferencesGetCollection,
  getApiReferencesGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import type {
  ApiReferencesGetCollectionParams,
  ReferenceReferenceReadModel
} from '@r/api/generated/client.schemas.ts';

/** Le back plafonne perPage à 30 sur GET /references. */
export const REFERENCES_MAX_PAGE_SIZE = 30;

export type AllReferencesParams = Omit<ApiReferencesGetCollectionParams, 'page' | 'perPage'>;

/**
 * Récupère toutes les pages de GET /references : la 1re page donne `pagination.last`,
 * les suivantes partent ensuite en parallèle.
 */
export async function fetchAllReferences(
  params?: AllReferencesParams,
  signal?: AbortSignal
): Promise<ReferenceReferenceReadModel[]> {
  const fetchPage = (page: number) =>
    apiReferencesGetCollection(
      { ...params, page, perPage: REFERENCES_MAX_PAGE_SIZE },
      undefined,
      signal
    );

  const firstPage = await fetchPage(1);
  const otherPages = await Promise.all(
    Array.from({ length: Math.max(0, firstPage.pagination.last - 1) }, (_, i) => fetchPage(i + 2))
  );

  return [firstPage, ...otherPages].flatMap((pageData) => pageData.data ?? []);
}

/**
 * Liste complète des références (sélecteurs, filtres, listes non paginées).
 * La clé reste sous le préfixe `/references` : invalider ce préfixe rafraîchit aussi ce cache.
 */
export function useAllReferences(params?: AllReferencesParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...getApiReferencesGetCollectionQueryKey(params), 'all'],
    queryFn: ({ signal }) => fetchAllReferences(params, signal),
    placeholderData: keepPreviousData,
    enabled: options?.enabled
  });
}
